import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { usePrefs, useLabels, useRequestData } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { detailTabClickEvent } from '../utils/wueTracking';
import OFFER_TYPE_LABEL_KEYS from '../utils/incentiveTypeMap';

const getTabLabel = (incentiveTypeKey, prefs, labels, offers) => {
	if (prefs.useConditionalCategoryTypes === 'true') {
		const offerWithCategory = offers.find(
			(o) =>
				o.incentiveType.toLowerCase() === incentiveTypeKey &&
				o.conditionalCategory
		);
		if (offerWithCategory?.conditionalCategory) {
			return offerWithCategory.conditionalCategory;
		}
	}

	const labelKey = OFFER_TYPE_LABEL_KEYS[incentiveTypeKey];
	if (labelKey) {
		const label = labels.get(labelKey);
		if (label !== labelKey) return label;
	}

	return incentiveTypeKey;
};

const deriveAvailableTabs = (offers, prefs, labels) => {
	const typeCount = {};

	offers.forEach((offer) => {
		const incentiveTypeKey = offer.incentiveType.toLowerCase();
		if (!typeCount[incentiveTypeKey]) {
			typeCount[incentiveTypeKey] = { incentiveTypeKey, count: 0 };
		}
		typeCount[incentiveTypeKey].count += 1;
	});

	const tabOrder = ['cash', 'cash_and_apr', 'apr', 'lease', 'promotion'];

	return tabOrder
		.filter((incentiveTypeKey) => typeCount[incentiveTypeKey]?.count > 0)
		.map((incentiveTypeKey) => ({
			incentiveTypeKey,
			label: getTabLabel(incentiveTypeKey, prefs, labels, offers),
			count: typeCount[incentiveTypeKey].count
		}));
};

const OfferTypeTabs = ({ offers, activeTab, onTabSelect, children }) => {
	const prefs = usePrefs();
	const labels = useLabels();
	const { widgetName, windowId } = useRequestData();
	const tabRefs = useRef({});
	const availableTabs = deriveAvailableTabs(offers, prefs, labels);

	const handleKeyDown = useCallback(
		(e) => {
			if (!['ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(e.key)) {
				return;
			}

			e.preventDefault();

			const currentIndex = availableTabs.findIndex(
				(t) => t.incentiveTypeKey === e.currentTarget.dataset.tabkey
			);

			if (e.key === 'ArrowRight') {
				const nextIndex = (currentIndex + 1) % availableTabs.length;
				const nextKey = availableTabs[nextIndex].incentiveTypeKey;
				tabRefs.current[nextKey]?.focus();
			} else if (e.key === 'ArrowLeft') {
				const prevIndex =
					(currentIndex - 1 + availableTabs.length) %
					availableTabs.length;
				const prevKey = availableTabs[prevIndex].incentiveTypeKey;
				tabRefs.current[prevKey]?.focus();
			} else if (e.key === 'Enter' || e.key === ' ') {
				const tab = availableTabs[currentIndex];
				if (tab.incentiveTypeKey !== activeTab) {
					trackEvent(
						widgetName,
						windowId,
						detailTabClickEvent({ tabLabel: tab.label })
					);
					onTabSelect(tab.incentiveTypeKey);
				}
			}
		},
		[availableTabs, activeTab, onTabSelect, widgetName, windowId]
	);

	if (availableTabs.length === 0) return null;

	return (
		<div className="offer-type-tabs mt-4">
			<ul
				className="nav nav-tabs"
				role="tablist"
				aria-label="Offer type filters"
			>
				{availableTabs.map((tab) => {
					const isActive = tab.incentiveTypeKey === activeTab;
					return (
						<li className="nav-item" key={tab.incentiveTypeKey}>
							<button
								type="button"
								role="tab"
								id={`tab-${tab.incentiveTypeKey}`}
								className={`nav-link${
									isActive ? ' active' : ''
								}`}
								aria-selected={isActive ? 'true' : 'false'}
								aria-controls={`tabpanel-${tab.incentiveTypeKey}`}
								tabIndex={isActive ? 0 : -1}
								data-tabkey={tab.incentiveTypeKey}
								ref={(el) => {
									tabRefs.current[tab.incentiveTypeKey] = el;
								}}
								onClick={() => {
									if (!isActive) {
										trackEvent(
											widgetName,
											windowId,
											detailTabClickEvent({
												tabLabel: tab.label
											})
										);
										onTabSelect(tab.incentiveTypeKey);
									}
								}}
								onKeyDown={handleKeyDown}
							>
								{tab.label}
							</button>
						</li>
					);
				})}
			</ul>

			{/* aria-live="polite": validates with real screen readers (NVDA, VoiceOver)
				   to confirm no double-announcement with native tabpanel handling */}
			<div
				role="tabpanel"
				id={`tabpanel-${activeTab}`}
				aria-labelledby={`tab-${activeTab}`}
				aria-live="polite"
				className="offer-tab-content mt-3"
			>
				{children}
			</div>
		</div>
	);
};

OfferTypeTabs.propTypes = {
	offers: PropTypes.arrayOf(
		PropTypes.shape({
			incentiveId: PropTypes.string.isRequired,
			incentiveType: PropTypes.string.isRequired,
			conditionalCategory: PropTypes.string
		})
	).isRequired,
	activeTab: PropTypes.string,
	onTabSelect: PropTypes.func.isRequired,
	children: PropTypes.node
};

OfferTypeTabs.defaultProps = {
	activeTab: null,
	children: null
};

export { deriveAvailableTabs };
export default OfferTypeTabs;
